// A creator's page (/c/<cr_id>), in Studio Row's colours: a banner built from their games' art (their name in type
// when there's none), who they are (name, links, follower count), Follow as the one accent, then what they made:
// games, assets and clips of their games. Every module with nothing in it stays hidden. Most creators are listed from
// the web and haven't claimed their page: it reads from their games alone and never invents a bio; a quiet
// "Is this you?" offers the claim.

import { api } from "../api.ts";
import { findCreator, getCatalogue, type Game } from "../catalogue.ts";
import { type Clip, clipCard } from "../clips.ts";
import { append, h, link } from "../dom.ts";
import { claimHref } from "../listing.ts";
import { setTitle } from "../router.ts";
import { currentMe } from "../session.ts";
import { selling, visibleItems } from "../selling.ts";
import { ownedAssets, store } from "../store-client.ts";
import { creatorStorePath } from "../store.ts";
import { reducedMotion, zoneVars } from "../tokens.ts";
import { studioBangers } from "../people.ts";
import { posterCard } from "./home.ts";
import { assetGrid } from "./store.ts";
import { zoneById, zoneHref } from "../zones/registry.ts";
import { artFirst, displayName, isHostCreator, linkLabel, madeLine } from "../zones/creators/data.ts";
import { creatorProfile, followButton } from "../zones/creators/follow.ts";
import { ensureCreatorsStyle } from "../zones/creators/style.ts";
import { studioBanner } from "../zones/creators/ui.ts";

export { followButton, followMark, setFollow } from "../zones/creators/follow.ts";

export async function creatorPage(params: Record<string, string>): Promise<HTMLElement> {
  const cat = await getCatalogue();
  // creators with only a handle (no game yet) aren't in the catalogue's list
  const found = await findCreator(params.id ?? "");
  if (!found) throw new Error("No creator by that name on Banger.");
  const c = found.creator;
  const name = displayName(c);
  setTitle(name);
  ensureCreatorsStyle();
  const games = artFirst(cat.games.filter((g) => g.creator.id === c.id && !g.firstParty));
  const site = linkLabel(c.url);
  const zone = zoneById("creators");

  const host = isHostCreator(c, games);
  // the creator page wears Studio Row: a `.zone` root, so the shell's games-search strip steps back and the banner
  // runs full bleed (review 2026-10-19 #5)
  const root = h("article", { class: "zone cr-page", "data-zone": "creators", "data-motion": reducedMotion() ? "still" : "calm" });
  if (zone) for (const [k, v] of Object.entries(zoneVars(zone))) root.style.setProperty(k, v);
  document.body.dataset.zone = "creators";
  const bio = h("p", { class: "cr-bio", hidden: true });
  const links = h("span", { class: "cr-links", hidden: true });

  append(root, [
    h(
      "header",
      { class: "cr-head" },
      studioBanner(c.name, games),
      h(
        "div",
        { class: "cr-id" },
        zone ? link(zoneHref(zone), { class: "cr-eyebrow cr-row-link" }, "Studio Row") : null,
        h("div", { class: "cr-name-row" }, h("h1", { class: `cr-name${host ? " host" : ""}` }, name)),
        h(
          "p",
          { class: "cr-facts" },
          games.length ? h("span", null, madeLine(games)) : null,
          c.claimedBy ? h("span", { class: "cr-verified" }, "Verified creator") : null,
          host ? h("span", null, "Found on the web") : null,
          site && !host ? h("a", { href: site.href, rel: "noopener noreferrer", target: "_blank", class: "cr-site" }, site.label) : null,
          links,
        ),
        followButton(c.id, name, { count: true }),
      ),
    ),
    h("section", { class: "cr-section cr-about" }, bio),
    games.length ? gamesSection(games) : null,
    // BANGERS.md slice 1: what the developer posted as this studio (hidden when empty; the claimer gets the composer)
    studioBangers(c.id, c.claimedBy ?? null),
    creatorAssets(c.id),
    games.length ? creatorClips(games) : null,
    c.claimedBy ? null : claimNote(c.id, games.length > 0),
  ]);
  // what a claimant wrote (accounts' creator profile): shown when present, as text, https links only. The avatar
  // isn't shown (a hotlinked image; see parseProfile).
  void creatorProfile(c.id).then((p) => {
    if (p.bio) {
      bio.textContent = p.bio;
      bio.hidden = false;
    }
    const extra = p.links.filter((l) => l.url !== site?.href);
    if (extra.length) {
      links.replaceChildren(...extra.map((l, i) => [i ? " · " : "", h("a", { href: l.url, rel: "noopener noreferrer", target: "_blank", class: "cr-site" }, l.label)]).flat());
      links.hidden = false;
    }
  });
  return root;
}

function gamesSection(games: Game[]): HTMLElement {
  return h(
    "section",
    { class: "cr-section", "aria-labelledby": "cr-games-title" },
    h("h2", { class: "cr-h2", id: "cr-games-title" }, games.length === 1 ? "What they made" : "Their games"),
    // no orphan tile: up to 8 games sit in even rows (4 + 4, 3 + 3 …); more flow
    withCols(h("ul", { class: `cr-games${games.length === 1 ? " one" : ""}`, role: "list" }, games.map((g) => posterCard(g))), games.length),
  );
}

function withCols(ul: HTMLElement, n: number): HTMLElement {
  if (n >= 2 && n <= 8) ul.style.setProperty("--cols", String(n <= 4 ? n : Math.ceil(n / 2)));
  return ul;
}

/** "Is this you?": quiet, under everything, for an unclaimed page. */
function claimNote(creatorId: string, hasGames: boolean): HTMLElement {
  return h(
    "p",
    { class: "cr-claim" },
    "Is this you? ",
    link(claimHref({ creator: { id: creatorId, name: "", url: null, claimedBy: null } }), null, "Claim this page"),
    hasGames ? " to add a bio, post devlogs and see who plays." : " to add a bio and your games.",
  );
}

/** The creator's store assets (a store handle publishes under cr_<handle>); hidden when there are none. */
function creatorAssets(creatorId: string): HTMLElement | null {
  if (!creatorId.startsWith("cr_")) return null;
  const el = h("section", { class: "cr-section", hidden: true });
  void (async () => {
    const b = await store();
    const c = await b.creator(creatorId.slice(3));
    const shown = c ? visibleItems(c.assets, selling()) : []; // paid items leave the lists without selling (wave 9)
    if (!c || !shown.length) return el.remove();
    const owned = await ownedAssets(currentMe()?.id ?? null);
    el.replaceChildren(h("div", { class: "cr-section-head" }, h("h2", { class: "cr-h2" }, "Assets"), link(creatorStorePath(c.handle), { class: "cr-more-link" }, "All assets")), assetGrid(shown.slice(0, 8), owned));
    el.hidden = false;
  })().catch(() => el.remove());
  return el;
}

/** Players' clips of their games (community), newest first; hidden when there are none. */
function creatorClips(games: Game[]): HTMLElement {
  const el = h("section", { class: "cr-section", hidden: true });
  void (async () => {
    const lists = await Promise.all(
      games.slice(0, 6).map((g) =>
        api<{ clips?: Clip[] }>("community", `/games/${encodeURIComponent(g.id)}/clips?limit=4`, { auth: false })
          .then((r) => (r.clips ?? []).map((c) => ({ c, g })))
          .catch(() => []),
      ),
    );
    const all = lists.flat().sort((a, b) => b.c.created_at - a.c.created_at).slice(0, 6);
    if (!all.length) return el.remove();
    el.replaceChildren(h("h2", { class: "cr-h2" }, "Clips from players"), h("ul", { class: "clip-grid" }, all.map(({ c, g }) => clipCard(c, { id: g.id, title: g.title, play: "" }))));
    el.hidden = false;
  })();
  return el;
}
